import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

import {
  megaventoryProductEditUrl,
  megaventoryPurchaseInvoiceDocumentUrl,
  megaventoryPurchaseOrderDocumentUrl,
} from "@/lib/megaventoryDocumentUrls";

export function MegaventoryProductExternalLink({
  productMvId,
  sku,
  className,
}: {
  productMvId: number;
  sku: string | null;
  className?: string;
}): JSX.Element {
  const label = sku?.trim() || `MV ${productMvId}`;
  return (
    <a
      href={megaventoryProductEditUrl(productMvId, sku)}
      target="_blank"
      rel="noreferrer noopener"
      className={className ?? "inline-flex shrink-0 text-slate-400 hover:text-brand-600"}
      aria-label={`Open product ${label} in Megaventory`}
    >
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

export function MegaventoryPurchaseOrderExternalLink({
  purchaseOrderMvId,
  poNumber,
  className,
}: {
  purchaseOrderMvId: number;
  poNumber: string | null;
  className?: string;
}): JSX.Element {
  const label = poNumber?.trim() || `MV ${purchaseOrderMvId}`;
  return (
    <a
      href={megaventoryPurchaseOrderDocumentUrl(purchaseOrderMvId, poNumber)}
      target="_blank"
      rel="noreferrer noopener"
      className={className ?? "inline-flex shrink-0 text-slate-400 hover:text-brand-600"}
      aria-label={`Open purchase order ${label} in Megaventory`}
    >
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

export function PurchaseOrderHubLinkWithMegaventory({
  purchaseOrderId,
  poNumber,
  purchaseOrderMvId,
  systemOfRecord,
}: {
  purchaseOrderId: number;
  poNumber: string | null;
  purchaseOrderMvId: number | null;
  /** Hub-owned POs are no longer editable in Megaventory; omit the external glyph. */
  systemOfRecord?: "hub" | "megaventory" | string | null;
}): JSX.Element {
  const label = poNumber?.trim() || `PO #${purchaseOrderId}`;
  const showMegaventoryLink =
    purchaseOrderMvId != null && String(systemOfRecord ?? "").trim().toLowerCase() !== "hub";
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <Link
        to={`/mrp/purchase-orders/${purchaseOrderId}`}
        className="font-mono text-brand-700 hover:text-brand-900 hover:underline"
      >
        {label}
      </Link>
      {showMegaventoryLink ? (
        <MegaventoryPurchaseOrderExternalLink purchaseOrderMvId={purchaseOrderMvId} poNumber={poNumber} />
      ) : null}
    </span>
  );
}

export function MegaventoryPurchaseInvoiceExternalLink({
  purchaseInvoiceMvId,
  mvNumber,
  className,
}: {
  purchaseInvoiceMvId: number;
  mvNumber: string | null;
  className?: string;
}): JSX.Element {
  const label = mvNumber?.trim() || `MV ${purchaseInvoiceMvId}`;
  return (
    <a
      href={megaventoryPurchaseInvoiceDocumentUrl(purchaseInvoiceMvId, mvNumber)}
      target="_blank"
      rel="noreferrer noopener"
      className={className ?? "inline-flex shrink-0 text-slate-400 hover:text-brand-600"}
      aria-label={`Open purchase invoice ${label} in Megaventory`}
    >
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </a>
  );
}

/** @deprecated Use PurchaseOrderHubLinkWithMegaventory */
export const PurchaseOrderLineHubLinkWithMegaventory = PurchaseOrderHubLinkWithMegaventory;
